import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertOctagon, AlertTriangle, ArrowRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { isReleaseFlagOn } from "@/config/featureFlags";
import { useTeacherEntitlements } from "@/hooks/use-teacher-entitlements";
import { useMyOfferings } from "@/hooks/useMyOfferings";

/**
 * F18 (ACs 53-56) — the teacher-facing notice for an active offering-limit
 * grace period. Shared across the teacher dashboard, offerings page, and
 * subscription page: rendered wherever a page-level notice belongs, gated on
 * `entitlements.grace.active`.
 *
 * This is the *notice*, not the enforcement — the 30-day clock and the actual
 * auto-unpublish are entirely server-side (AC#53, AC#55). Getting the copy
 * wrong here doesn't change what gets unpublished, but it can leave a teacher
 * finding out only after the fact, which is exactly what AC#56 forbids.
 */

const URGENT_WITHIN_DAYS = 7;

const OFFERING_TYPE_LABELS: Record<string, string> = {
  ONE_TO_ONE: "1:1",
  GROUP: "Group",
  CRASH_COURSE: "Crash Course",
};

/** Whole calendar days remaining until (and including) the deadline date. */
function daysUntil(deadlineIso: string): number {
  const deadline = new Date(deadlineIso);
  deadline.setHours(23, 59, 59, 999);
  const now = new Date();
  return Math.ceil((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

function formatDeadline(deadlineIso: string): string {
  const date = new Date(deadlineIso);
  if (Number.isNaN(date.getTime())) return deadlineIso;
  return date.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}

function daysLabel(daysLeft: number): string {
  if (daysLeft <= 0) return "today";
  if (daysLeft === 1) return "1 day";
  return `${daysLeft} days`;
}

/** Dismissal is per browser session and per grace period -- a new deadline (a
 * later downgrade re-triggering grace) must show the banner again even if an
 * earlier one was dismissed. */
function dismissKeyFor(deadlineIso: string): string {
  return `teacher_grace_banner_dismissed_${deadlineIso}`;
}

export function GracePeriodBanner() {
  const navigate = useNavigate();
  const flagOn = isReleaseFlagOn("TEACHER_TIER_BENEFITS");
  const { data: entitlements } = useTeacherEntitlements();
  const grace = entitlements?.grace ?? null;
  const graceActive = flagOn && Boolean(grace?.active);

  // Only resolve offering titles once we know there's something to resolve --
  // most page visits never need this extra fetch.
  const { data: offerings } = useMyOfferings(graceActive);

  const deadline = grace?.deadline;
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    if (!deadline) return;
    try {
      setDismissed(sessionStorage.getItem(dismissKeyFor(deadline)) === "true");
    } catch {
      setDismissed(false);
    }
    // Re-check whenever the deadline changes (a fresh grace period).
  }, [deadline]);

  const excessOfferings = useMemo(() => {
    if (!grace || !offerings) return [];
    const idSet = new Set(grace.excessOfferingIds);
    return offerings.filter((o) => idSet.has(o.id));
  }, [grace, offerings]);

  if (!graceActive || !grace || dismissed) return null;

  const excessCount = grace.excessOfferingIds.length;
  const daysLeft = daysUntil(grace.deadline);
  const isUrgent = daysLeft <= URGENT_WITHIN_DAYS;
  const Icon = isUrgent ? AlertOctagon : AlertTriangle;

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(dismissKeyFor(grace.deadline), "true");
    } catch {
      // Session-only convenience -- safe to no-op if storage is unavailable.
    }
  };

  return (
    <div
      role="alert"
      className={cn(
        "rounded-2xl p-5 mb-6 transition-colors duration-200",
        isUrgent ? "border-2 border-amber-500 bg-amber-100" : "border-2 border-amber-300 bg-amber-50"
      )}
    >
      <div className="flex items-start gap-3">
        <Icon
          className={cn("w-5 h-5 shrink-0 mt-0.5 text-amber-700", isUrgent && "motion-safe:animate-pulse")}
          aria-hidden="true"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-bold text-amber-900">
              Grace period: {excessCount} offering{excessCount === 1 ? "" : "s"} over your plan&apos;s limit
            </p>
            {isUrgent && (
              <span className="inline-flex items-center text-xs font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-200 border border-amber-400 text-amber-900">
                Urgent — {daysLabel(daysLeft)} left
              </span>
            )}
          </div>

          <p className="text-sm text-amber-800 mt-1">
            On {formatDeadline(grace.deadline)}, the platform will automatically unpublish your excess
            offerings (disallowed types first, then newest-first) unless you act before then.
          </p>

          {excessOfferings.length > 0 && (
            <ul className="mt-3 flex flex-wrap gap-1.5" aria-label="Offerings over the limit">
              {excessOfferings.map((o) => (
                <li
                  key={o.id}
                  className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-white border border-amber-200 text-amber-900"
                >
                  <span className="text-amber-500">{OFFERING_TYPE_LABELS[o.offeringType] ?? o.offeringType}</span>
                  {o.title}
                </li>
              ))}
            </ul>
          )}

          <div className="flex flex-wrap items-center gap-3 mt-4">
            <p className="text-sm text-amber-800">
              Unpublish {excessCount === 1 ? "this offering" : `${excessCount} offerings`} yourself to get back
              under your limit, or
            </p>
            <Button
              onClick={() => navigate("/teacher-subscription")}
              size="sm"
              className="shrink-0 cursor-pointer bg-amber-600 hover:bg-amber-700 text-white transition-colors duration-200"
            >
              Upgrade your plan
              <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
            </Button>
          </div>
        </div>

        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss for this session"
          className="w-6 h-6 flex items-center justify-center rounded text-amber-700 hover:text-amber-900 hover:bg-amber-900/10 transition-colors duration-200 shrink-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600 focus-visible:ring-offset-1"
        >
          <X className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
